/**
 * App Component Styles
 */

.app {
  min-height: var(--tg-viewport-stable-height, 100vh);
  display: flex;
  flex-direction: column;
  background: var(--tg-theme-bg-color);
}

/* Chart View */
.chartView {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md);
  padding-bottom: var(--spacing-xl);
  gap: var(--spacing-lg);
}

/* Info Panel */
.infoPanel {
  background: var(--tg-theme-section-bg-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  position: relative;
  animation: slideUp var(--transition-normal);
}

.closeBtn {
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  background: var(--tg-theme-secondary-bg-color);
  color: var(--tg-theme-hint-color);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  transition: all var(--transition-fast);
}

.closeBtn:hover {
  background: var(--tg-theme-destructive-text-color);
  color: white;
  transform: scale(1.1);
}

.infoPanel h3 {
  margin-bottom: var(--spacing-md);
  color: var(--tg-theme-text-color);
  font-size: var(--font-size-lg);
}

.infoGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--spacing-md);
}

.infoItem {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.infoItem .label {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.infoItem .value {
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
  font-weight: 600;
}

/* Matrix Button */
.matrixButton {
  width: 100%;
  padding: 12px 16px;
  background: var(--tg-theme-section-bg-color);
  border: 1px solid var(--tg-theme-hint-color, #444);
  border-radius: var(--border-radius);
  color: var(--tg-theme-text-color);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: opacity 0.2s;
}

.matrixButton:active {
  opacity: 0.7;
}

/* Balance Section */
.balanceSection {
  background: var(--tg-theme-section-bg-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.balanceSection h4 {
  margin-bottom: var(--spacing-md);
  font-size: var(--font-size-md);
  color: var(--tg-theme-text-color);
  font-weight: 600;
}

.balanceBar {
  display: flex;
  height: 32px;
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-sm);
}

.balanceSegment {
  transition: flex var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: var(--font-size-sm);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  position: relative;
}

.balanceSegment::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.2),
    transparent 50%,
    rgba(0, 0, 0, 0.1)
  );
}

.balanceSegment.fire {
  background: linear-gradient(135deg, #ff6b6b, #ff8787);
}

.balanceSegment.earth {
  background: linear-gradient(135deg, #51cf66, #69db7c);
}

.balanceSegment.air {
  background: linear-gradient(135deg, #ffd43b, #ffe066);
}

.balanceSegment.water {
  background: linear-gradient(135deg, #74c0fc, #91d7ff);
}

.balanceLabels {
  display: flex;
  justify-content: space-around;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.balanceLabels span {
  font-size: var(--font-size-sm);
  color: var(--tg-theme-hint-color);
  font-weight: 500;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--tg-theme-secondary-bg-color);
  border-radius: var(--border-radius-sm);
}

/* Loading Overlay */
.loadingOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  z-index: 1000;
  animation: fadeIn var(--transition-fast);
}

.loadingOverlay p {
  color: white;
  font-size: var(--font-size-lg);
  margin: 0;
}

.loadingSpinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error Toast */
.errorToast {
  position: fixed;
  bottom: var(--spacing-lg);
  left: 50%;
  transform: translateX(-50%);
  background: var(--tg-theme-destructive-text-color);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  cursor: pointer;
  animation: fadeIn var(--transition-normal);
  font-weight: 500;
}

/* Warning Toast */
.warningToast {
  position: fixed;
  bottom: var(--spacing-lg);
  left: 50%;
  transform: translateX(-50%);
  background: #f59e0b;
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  cursor: pointer;
  animation: fadeIn var(--transition-normal);
  font-weight: 500;
}

/* Loading container (initial load) */
.loadingContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: var(--tg-viewport-stable-height, 100vh);
  gap: var(--spacing-md);
}

.loadingContainer p {
  color: var(--tg-theme-hint-color);
  font-size: var(--font-size-md);
}
